import getCategories from '@/services/CategoryService/getCategories'
import getPricingList from '@/services/PricingServices/getPricingList'
import getAllProducts from '@/services/ProductService/getAllProducts'
import getViewProfiles from '@/services/ProductService/getViewProfiles'
import getSettings from '@/services/ThemeService/getSettings'
import { ProductData } from '@/types/product'
import { Setting } from '@/types/theme'

import { OnboardingSignals } from '../types'

const getSettledValue = <T,>(result: PromiseSettledResult<T>): T | null => (
  result.status === 'fulfilled' ? result.value : null
)

const hasValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0
  return true
}

const hasPublishedTheme = (settings: Setting[]) => {
  const value = settings.find((setting) => setting.key === 'theme')?.value
  if (typeof value === 'string') return value.trim().length > 0
  if (!value || typeof value !== 'object') return false
  return typeof value.id === 'string' && value.id.trim().length > 0
}

export const emptyOnboardingSignals: OnboardingSignals = {
  storeConfigured: null,
  graphicCategoryCreated: null,
  fontAdded: null,
  viewProfileCreated: null,
  pricingModelCreated: null,
  sectionCreated: null,
  themePublished: null,
}

const loadOnboardingSignals = async (): Promise<OnboardingSignals> => {
  const [settingsResult, categoriesResult, pricingResult, viewProfilesResult, productsResult] = await Promise.allSettled([
    getSettings(),
    getCategories(),
    getPricingList(),
    getViewProfiles(),
    getAllProducts(),
  ])

  const settings = getSettledValue(settingsResult) as Setting[] | null
  const categories = getSettledValue(categoriesResult)
  const pricingModels = getSettledValue(pricingResult)
  const viewProfiles = getSettledValue(viewProfilesResult)
  const products = getSettledValue(productsResult) as ProductData[] | null

  const configuredSettingKeys = [
    'machine',
    'profile_version',
    'company_logo',
    'brand_color',
    'erp_integration',
    'customization_element_size',
    'profanity_rules',
  ]
  const fontList = settings?.find((setting) => setting.key === 'fontList')?.value

  return {
    storeConfigured: settings === null
      ? null
      : settings.some((setting) => configuredSettingKeys.includes(setting.key) && hasValue(setting.value)),
    graphicCategoryCreated: categories === null
      ? null
      : categories.some((item) => item.type === 'category'),
    fontAdded: settings === null ? null : Array.isArray(fontList) && fontList.length > 0,
    viewProfileCreated: viewProfiles === null ? null : viewProfiles.length > 0,
    pricingModelCreated: pricingModels === null ? null : pricingModels.length > 0,
    sectionCreated: products === null
      ? false
      : products.some((product) => Array.isArray(product.sections) && product.sections.length > 0),
    themePublished: settings === null ? null : hasPublishedTheme(settings),
  }
}

export default loadOnboardingSignals
